import { httpClient } from '@/core/api/httpClient';
import { toPascalCaseParams } from '@/shared/lib/to-pascal-case-params';
import type {
  Task,
  TaskApiParams,
  TaskQueryResult,
  CreateTaskPayload,
  UpdateTaskDetailPayload,
  CreateSubtaskPayload,
  AttachTagPayload,
  TaskDetail,
  TaskAttachment,
  TaskSummaryDto,
  PendingTaskDtoPagedResult,
  TaskDetailDto,
  AttachmentDto,
  TaskTagDto,
  TaskTreeNode,
  CreateTaskApiRequest,
  UpdateTaskApiRequest,
  MoveTaskApiRequest,
  AssignTaskApiRequest,
  ChangeTaskPriorityApiRequest,
  ReorderTasksApiRequest,
  CreateSubtaskApiRequest,
  MyPendingTasksParams,
  ActivityDtoPagedResult,
  TaskActivityParams,
  BoardActivityParams,
  BoardActivityDateRangeParams,
  BoardActivitySummaryDto,
  TaskCommentDtoPagedResult,
  TaskCommentParams,
  AddTaskCommentPayload,
  CopyTaskApiRequest,
  CopyTaskResponse,
  BulkEditTasksApiRequest,
  BulkEditTasksResponse,
  BulkDeleteTasksApiRequest,
  BulkDeleteTasksResponse,
  ImportTasksResponse,
  TimelineDto,
  TimelineParams,
} from '../types';
import {
  adaptTaskSummaryDto,
  adaptTaskDetailDto,
  adaptCreatePayload,
  adaptUpdatePayload,
  adaptCreateSubtaskPayload,
} from '../adapters';
import { getMockQueryResult } from './tasks-paged.mock';
import { getMockTaskDetail, getMockAttachments, getMockActivityResult } from './taskDetail.mock';
import { getMockBoardActivityResult, getMockBoardActivitySummary } from './boardActivity.mock';
import { getMockTimelineResult } from './timeline.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function deriveMimeType(mime: string | null | undefined): TaskAttachment['type'] {
  if (!mime) return 'other';
  if (mime.startsWith('image/')) return 'image';
  if (
    mime === 'application/pdf' ||
    mime.startsWith('application/msword') ||
    mime.startsWith('application/vnd.openxmlformats-officedocument') ||
    mime.startsWith('application/vnd.ms-') ||
    mime === 'text/plain' ||
    mime === 'text/csv'
  )
    return 'document';
  return 'other';
}

function adaptAttachmentDto(dto: AttachmentDto): TaskAttachment {
  const fileUrl = `${env.VITE_API_URL}${API_V1}/files/${dto.storageKey}`;
  const type = deriveMimeType(dto.mimeType);
  return {
    id: dto.id,
    taskId: dto.taskId,
    name: dto.fileName,
    url: fileUrl,
    thumbnailUrl: type === 'image' ? fileUrl : null,
    type,
    size: Number(dto.fileSize),
    uploadedAt: dto.createdAt,
    uploadedBy: null,
  };
}

// ------------------------------------------------------------------
// GET
// ------------------------------------------------------------------

async function getQuery(
  projectId: string,
  params: TaskApiParams,
  signal?: AbortSignal,
): Promise<TaskQueryResult> {
  if (env.VITE_USE_MOCK === 'true') {
    return getMockQueryResult(projectId, params);
  }
  const rawParams: Record<string, unknown> = {
    filters: params.filters,
    sort: params.sort,
    groupBy: params.groupBy !== 'none' ? params.groupBy : undefined,
    groupKey: params.groupKey,
    cursor: params.cursor,
    limit: params.limit,
    view: params.view,
    search: params.search,
    dateStart: params.dateStart,
    dateEnd: params.dateEnd,
    includeArchived: params.includeArchived,
    boardId: params.boardId,
    archivedOnly: params.archivedOnly,
    topLevelOnly: params.topLevelOnly,
  };
  const { data } = await httpClient.get<TaskQueryResult>(`${API_V1}/projects/${projectId}/tasks`, {
    params: toPascalCaseParams(rawParams),
    signal,
  });
  return data;
}

async function getById(taskId: string, signal?: AbortSignal): Promise<TaskDetail> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve(getMockTaskDetail(taskId));
  }
  const { data } = await httpClient.get<TaskDetailDto>(`${API_V1}/tasks/${taskId}`, { signal });
  return adaptTaskDetailDto(data);
}

async function getTaskTree(taskId: string, signal?: AbortSignal): Promise<TaskTreeNode> {
  const { data } = await httpClient.get<TaskTreeNode>(`${API_V1}/tasks/${taskId}/tree`, { signal });
  return data;
}

async function getSubtasks(taskId: string, signal?: AbortSignal): Promise<Task[]> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve(
      getMockTaskDetail(taskId).subtasks?.map((t) => ({ ...t, projectId: 'mock' })) ?? [],
    );
  }
  const { data } = await httpClient.get<TaskSummaryDto[]>(`${API_V1}/tasks/${taskId}/subtasks`, {
    signal,
  });
  return data.map(adaptTaskSummaryDto);
}

async function listAttachments(taskId: string, signal?: AbortSignal): Promise<TaskAttachment[]> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve(getMockAttachments(taskId));
  }
  const { data } = await httpClient.get<AttachmentDto[]>(`${API_V1}/tasks/${taskId}/attachments`, {
    signal,
  });
  return data.map(adaptAttachmentDto);
}

async function getTaskTags(taskId: string, signal?: AbortSignal): Promise<TaskTagDto[]> {
  const { data } = await httpClient.get<TaskTagDto[]>(`${API_V1}/tasks/${taskId}/tags`, { signal });
  return data;
}

async function getMyPendingTasks(
  params: MyPendingTasksParams = {},
  signal?: AbortSignal,
): Promise<PendingTaskDtoPagedResult> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve({
      items: [],
      page: 1,
      pageSize: params.PageSize ?? 10,
      totalCount: 0,
      totalPages: 1,
      hasPreviousPage: false,
      hasNextPage: false,
      firstItemOnPage: 0,
      lastItemOnPage: 0,
    });
  }
  const { data } = await httpClient.get<PendingTaskDtoPagedResult>(`${API_V1}/me/tasks/pending`, {
    params,
    signal,
  });
  return data;
}

async function getTaskActivity(
  taskId: string,
  params: TaskActivityParams = {},
  signal?: AbortSignal,
): Promise<ActivityDtoPagedResult> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve(getMockActivityResult(taskId, params.Page ?? 1, params.PageSize ?? 20));
  }
  const { data } = await httpClient.get<ActivityDtoPagedResult>(
    `${API_V1}/tasks/${taskId}/activity`,
    {
      params,
      signal,
    },
  );
  return data;
}

async function getBoardActivity(
  projectId: string,
  boardId: string,
  params: BoardActivityParams = {},
  signal?: AbortSignal,
): Promise<ActivityDtoPagedResult> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve(getMockBoardActivityResult(boardId, params));
  }
  const { data } = await httpClient.get<ActivityDtoPagedResult>(
    `${API_V1}/projects/${projectId}/boards/${boardId}/activity`,
    { params, signal },
  );
  return data;
}

async function getBoardActivitySummary(
  projectId: string,
  boardId: string,
  params: BoardActivityDateRangeParams,
  signal?: AbortSignal,
): Promise<BoardActivitySummaryDto> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve(getMockBoardActivitySummary());
  }
  const { data } = await httpClient.get<BoardActivitySummaryDto>(
    `${API_V1}/projects/${projectId}/boards/${boardId}/activity/summary`,
    { params, signal },
  );
  return data;
}

async function getTimeline(
  projectId: string,
  boardId: string,
  params: TimelineParams,
  signal?: AbortSignal,
): Promise<TimelineDto> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve(getMockTimelineResult(params));
  }
  const { data } = await httpClient.get<TimelineDto>(
    `${API_V1}/projects/${projectId}/boards/${boardId}/timeline`,
    { params, signal },
  );
  return data;
}

async function getTaskComments(
  taskId: string,
  params: TaskCommentParams = {},
  signal?: AbortSignal,
): Promise<TaskCommentDtoPagedResult> {
  const { data } = await httpClient.get<TaskCommentDtoPagedResult>(
    `${API_V1}/tasks/${taskId}/comments`,
    { params, signal },
  );
  return data;
}

async function addTaskComment(
  taskId: string,
  payload: AddTaskCommentPayload,
): Promise<{ id: string }> {
  const { data } = await httpClient.post<AddTaskCommentPayload, { id: string }>(
    `${API_V1}/tasks/${taskId}/comments`,
    payload,
  );
  return data;
}

async function deleteTaskComment(taskId: string, commentId: string): Promise<void> {
  await httpClient.delete(`${API_V1}/tasks/${taskId}/comments/${commentId}`);
}

// ------------------------------------------------------------------
// Mutations
// ------------------------------------------------------------------

async function create(projectId: string, payload: CreateTaskPayload): Promise<{ id: string }> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve({ id: `task-${Date.now()}` });
  }
  const { data } = await httpClient.post<CreateTaskApiRequest, { id: string }>(
    `${API_V1}/projects/${projectId}/tasks`,
    adaptCreatePayload(payload),
  );
  return data;
}

async function update(taskId: string, payload: UpdateTaskDetailPayload): Promise<void> {
  if (env.VITE_USE_MOCK === 'true') {
    return Promise.resolve();
  }
  const { core, sideEffects } = adaptUpdatePayload(payload);

  if (Object.keys(core).length > 0) {
    await httpClient.patch<UpdateTaskApiRequest, void>(`${API_V1}/tasks/${taskId}`, core);
  }

  const sideEffectPromises: Promise<unknown>[] = [];
  if (sideEffects.priority !== undefined) {
    sideEffectPromises.push(
      httpClient.patch<ChangeTaskPriorityApiRequest, void>(`${API_V1}/tasks/${taskId}/priority`, {
        priority: sideEffects.priority,
      }),
    );
  }
  if (sideEffects.statusId !== undefined || sideEffects.groupId !== undefined) {
    sideEffectPromises.push(
      httpClient.patch<MoveTaskApiRequest, void>(`${API_V1}/tasks/${taskId}/move`, {
        statusId: sideEffects.statusId ?? null,
        groupId: sideEffects.groupId ?? null,
      }),
    );
  }
  if (sideEffects.assigneeId !== undefined) {
    sideEffectPromises.push(
      httpClient.patch<AssignTaskApiRequest, void>(`${API_V1}/tasks/${taskId}/assign`, {
        assigneeId: sideEffects.assigneeId,
      }),
    );
  }
  await Promise.all(sideEffectPromises);
}

async function deleteTask(taskId: string): Promise<void> {
  await httpClient.delete(`${API_V1}/tasks/${taskId}`);
}

async function moveTask(taskId: string, payload: MoveTaskApiRequest): Promise<void> {
  await httpClient.patch<MoveTaskApiRequest, void>(`${API_V1}/tasks/${taskId}/move`, payload);
}

async function reorderTasks(payload: ReorderTasksApiRequest): Promise<void> {
  await httpClient.post<ReorderTasksApiRequest, void>(`${API_V1}/tasks/reorder`, payload);
}

async function createSubtask(
  taskId: string,
  payload: CreateSubtaskPayload,
): Promise<{ id: string }> {
  const { data } = await httpClient.post<CreateSubtaskApiRequest, { id: string }>(
    `${API_V1}/tasks/${taskId}/subtasks`,
    adaptCreateSubtaskPayload(payload),
  );
  return data;
}

async function uploadAttachment(taskId: string, file: File): Promise<{ id: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await httpClient.post<FormData, { id: string }>(
    `${API_V1}/tasks/${taskId}/attachments`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

async function deleteAttachment(taskId: string, attachmentId: string): Promise<void> {
  await httpClient.delete(`${API_V1}/tasks/${taskId}/attachments/${attachmentId}`);
}

async function attachTag(taskId: string, payload: AttachTagPayload): Promise<void> {
  await httpClient.post(`${API_V1}/tasks/${taskId}/tags`, payload);
}

async function detachTag(taskId: string, tagId: string): Promise<void> {
  await httpClient.delete(`${API_V1}/tasks/${taskId}/tags/${tagId}`);
}

// ------------------------------------------------------------------
// Advanced operations
// ------------------------------------------------------------------

async function archiveTask(taskId: string): Promise<void> {
  await httpClient.post<unknown, void>(`${API_V1}/tasks/${taskId}/archive`, {});
}

async function unarchiveTask(taskId: string): Promise<void> {
  await httpClient.post<unknown, void>(`${API_V1}/tasks/${taskId}/unarchive`, {});
}

async function copyTask(taskId: string, payload: CopyTaskApiRequest): Promise<CopyTaskResponse> {
  const { data } = await httpClient.post<CopyTaskApiRequest, CopyTaskResponse>(
    `${API_V1}/tasks/${taskId}/copy`,
    payload,
  );
  return data;
}

async function bulkEditTasks(payload: BulkEditTasksApiRequest): Promise<BulkEditTasksResponse> {
  const { data } = await httpClient.patch<BulkEditTasksApiRequest, BulkEditTasksResponse>(
    `${API_V1}/tasks/bulk`,
    payload,
  );
  return data;
}

async function bulkDeleteTasks(
  payload: BulkDeleteTasksApiRequest,
): Promise<BulkDeleteTasksResponse> {
  const { data } = await httpClient.delete<BulkDeleteTasksResponse>(
    `${API_V1}/tasks/bulk`,
    payload,
  );
  return data;
}

async function importTasks(projectId: string, formData: FormData): Promise<ImportTasksResponse> {
  const { data } = await httpClient.post<FormData, ImportTasksResponse>(
    `${API_V1}/projects/${projectId}/tasks/import`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

// ------------------------------------------------------------------
// Service export
// ------------------------------------------------------------------

export const tasksService = {
  getQuery,
  create,
  getById,
  getTaskTree,
  update,
  deleteTask,
  moveTask,
  reorderTasks,
  createSubtask,
  getSubtasks,
  listAttachments,
  uploadAttachment,
  deleteAttachment,
  attachTag,
  detachTag,
  getTaskTags,
  getMyPendingTasks,
  getTaskActivity,
  getBoardActivity,
  getBoardActivitySummary,
  getTimeline,
  getTaskComments,
  addTaskComment,
  deleteTaskComment,
  archiveTask,
  unarchiveTask,
  copyTask,
  bulkEditTasks,
  bulkDeleteTasks,
  importTasks,
};
